"use client";
/**
 * Kvalitetssikring / Quality control & documentation — Svendetid marketing
 * feature page.
 *
 * Built to mirror the reports and calendar feature pages on the shared
 * svendetid-ds design system: a live mockup in the hero, a "without / with"
 * comparison, three action-oriented feature blocks with mini mockups, a dark AI
 * block, a feature grid, a platform grid and a FAQ.
 *
 * Highlights the two flagship capabilities:
 *   1. Snap photos on site straight from the mobile app and upload them to the job.
 *   2. Collect a customer signature so they sign off on the completed work.
 *
 * Language-parametrized so /features/quality-control (da) and
 * /en/features/quality-control (en) share one source.
 */
import { useState } from "react";
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

const T = {
  da: {
    eyebrow: "Kvalitetssikring i Svendetid",
    h1a: "Dokumentér arbejdet — ",
    h1b: "med fotos",
    h1c: " og kundens underskrift",
    sub: "Tag billeder på pladsen direkte fra mobilappen, saml det hele på jobbet, og lad kunden skrive under på det færdige arbejde. Bevis for hvad der blev lavet, klar til at sende.",
    cta: "Kom i gang – det er gratis",
    seePricing: "Se priser",

    trust2: "Gratis at komme i gang",
    trust3: "Dansk support",
    // dashboard mockup labels
    mockReport: "Kvalitetssikring",
    mockTitle: "Dokumentation",
    mockPeriod: "Dette job",
    mockShare: "Del",
    // without / with
    cmpH2: "Bevis for arbejdet — ikke ord mod ord",
    withoutLabel: "Uden Svendetid",
    withLabel: "Med Svendetid",
    without: [
      "Billeder ligger spredt på folks private telefoner",
      "Ingen dokumentation når kunden klager bagefter",
      "Underskrifter på papir der bliver væk eller bliver våde",
      "Tvivl om hvad der faktisk blev udført og godkendt",
    ],
    with: [
      "Alle fotos samlet på det rigtige job — automatisk",
      "Tidsstemplet bevis for før, under og efter arbejdet",
      "Kundens underskrift fanget digitalt og gemt for evigt",
      "Et klart, delbart bevis på hvad der blev godkendt",
    ],
    // section intro
    introLabel: "Sådan virker det",
    introH2: "Dokumentation der følger med arbejdet",
    introSub: "Fotos. Underskrift. Samlet på jobbet.",
    // three feature blocks
    blocks: [
      {
        label: "Fotos fra pladsen",
        h3: "Tag billedet. Upload det. Direkte fra mobilen.",
        body: "Snap fotos på pladsen med appen og send dem direkte til jobbet — før, under og efter. Hvert billede tidsstemples og gemmes på den rigtige opgave, så du altid har bevis for hvad der blev lavet. Virker også offline; det uploader når der er forbindelse.",
        chips: [
          { icon: "camera-outline", text: "Tag foto" },
          { icon: "cloud-upload-outline", text: "Upload" },
          { icon: "phone-portrait-outline", text: "Fra mobilen" },
        ],
        mock: "photo",
      },
      {
        label: "Kundens underskrift",
        h3: "Lad kunden skrive under på det færdige arbejde",
        body: "Ræk telefonen eller tabletten til kunden og lad dem signere direkte på skærmen, når arbejdet er godkendt. Underskriften gemmes på jobbet med dato og tid — et digitalt sign-off der erstatter papir og fjerner enhver tvivl bagefter.",
        chips: [
          { icon: "create-outline", text: "Signér på skærm" },
          { icon: "checkmark-circle", text: "Godkendt" },
          { icon: "document-text-outline", text: "Gemt på jobbet" },
        ],
        mock: "sign",
      },
      {
        label: "Samlet & delbar",
        h3: "Al dokumentation på ét sted — klar til at sende",
        body: "Fotos, noter og underskrift samles på jobbet i den rækkefølge, det skete. Send et færdigt dokumentationsark til kunden, forsikringen eller bygherren med ét tryk — som bevis på kvaliteten og arbejdet, der blev udført.",
        chips: [
          { icon: "images-outline", text: "Foto-galleri" },
          { icon: "paper-plane-outline", text: "Send som PDF" },
          { icon: "lock-closed", text: "Gemt sikkert" },
        ],
        mock: "share",
      },
    ],
    // mock labels — photo mock
    mockJobLabel: "Job · Tagrenovering",
    mockPhotoBefore: "Før",
    mockPhotoDuring: "Under",
    mockPhotoAfter: "Efter",
    mockUploaded: "Uploadet",
    mockAddPhoto: "Tilføj foto",
    // sign mock
    mockSignTitle: "Godkendelse af arbejde",
    mockSignJob: "Tagrenovering · Nordby Bolig",
    mockSignBy: "Underskrevet af",
    mockSignName: "Anders Nordby",
    mockSignAt: "I dag 14:32",
    mockSignBtn: "Underskrevet",
    // share mock
    mockDocTitle: "Dokumentationsark",
    mockDocPhotos: "12 fotos",
    mockDocSigned: "Underskrevet",
    mockDocReady: "Klar",
    // AI block
    aiLabel: "AI-drevet kvalitetssikring",
    aiH2a: "Svendetid AI samler ",
    aiH2b: "dokumentationen",
    aiBody: "Spørg om hvad som helst: »Mangler vi afslutningsfotos på nogle af denne uges jobs?« eller »Hvilke job er færdige men ikke underskrevet?«. AI'en gennemgår alle dine jobs og fanger det, der mangler — før kunden gør det.",
    aiCta: "Prøv Svend AI",
    aiQuestion: "Hvilke færdige jobs mangler kundens underskrift?",
    aiAnswerIntro: "3 jobs er markeret færdige men mangler sign-off:",
    aiRow1: "Tagrenovering · Nordby Bolig",
    aiRow1Val: "12 fotos",
    aiRow2: "Badrenovering · Fam. Lund",
    aiRow2Val: "8 fotos",
    aiAnswerOutro: "Begge har fotos klar — vil du sende et underskrifts-link til kunden?",
    // feature grid
    gridH2: "Plus alt det, du skal bruge for at dokumentere kvaliteten",
    grid: [
      { icon: "camera-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Fotos fra mobilen", body: "Tag billeder på pladsen direkte i appen og upload dem til jobbet. Før, under og efter — altid på den rigtige opgave." },
      { icon: "create-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Digital underskrift", body: "Lad kunden skrive under på skærmen, når arbejdet er godkendt. Et digitalt sign-off der erstatter papir." },
      { icon: "time-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Tidsstemplet bevis", body: "Hvert foto og hver underskrift får automatisk dato og tid. Bevis for præcis hvad der blev lavet, og hvornår." },
      { icon: "images-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Samlet foto-galleri", body: "Al dokumentation samlet på jobbet i rækkefølge. Bladr gennem hele forløbet fra start til aflevering." },
      { icon: "paper-plane-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Send som dokument", body: "Generér et færdigt dokumentationsark og send det til kunde, forsikring eller bygherre med ét tryk." },
      { icon: "cloud-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Virker offline", body: "Tag fotos uden dækning på pladsen. Alt uploader automatisk, så snart der er forbindelse igen." },
    ],
    // platform
    platLabel: "Svendetid-platformen",
    platH2: "Kvalitetssikring er kun begyndelsen",
    platSub: "Kvalitetssikring er en del af platformen — jobs, tilbud, tid, kunder og fakturering i ét samlet system. Alt taler sammen.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Styr hvert job", href: "/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Tilbud", sub: "Vind flere opgaver", href: "/features/offer" },
      { icon: "calendar-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Kalender", sub: "Planlæg sjakket", href: "/features/calendar" },
      { icon: "bar-chart-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Rapporter", sub: "Overblik der virker", href: "/features/reports" },
      { icon: "people-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Kunder", sub: "Hele historikken", href: "/features/customer-database" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "Alle funktioner", sub: "Se hele platformen", href: "/features" },
    ],
    // security
    secLabel: "Sikkerhed hele vejen",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data i EU" },
      { icon: "chatbubbles-outline", text: "Support på dansk" },
    ],
    // FAQ
    faqH2: "Ofte stillede spørgsmål",
    faqs: [
      { q: "Hvordan tager jeg billeder fra mobilen?", a: "Åbn jobbet i Svendetid-appen og tryk på kameraet. Billedet tidsstemples og gemmes automatisk på den rigtige opgave. Du kan tage flere på rad — før, under og efter — og de samles ét sted." },
      { q: "Virker foto-upload uden internet?", a: "Ja. Du kan tage billeder på pladsen, selv uden dækning. De ligger lokalt på telefonen og uploader automatisk til jobbet, så snart der er forbindelse igen." },
      { q: "Hvordan får jeg kundens underskrift?", a: "Når arbejdet er godkendt, åbner du sign-off på telefonen eller tabletten og rækker den til kunden. De skriver under direkte på skærmen, og underskriften gemmes på jobbet med dato og tid." },
      { q: "Er den digitale underskrift gyldig som bevis?", a: "Underskriften gemmes sammen med navn, dato, tidspunkt og det dokumenterede arbejde. Det giver et klart, sporbart bevis for, at kunden godkendte det udførte arbejde — meget stærkere end en løs papirseddel." },
      { q: "Kan jeg sende dokumentationen videre?", a: "Ja. Saml fotos, noter og underskrift til ét dokumentationsark og send det til kunde, forsikring eller bygherre med ét tryk — som bevis på kvaliteten og det udførte arbejde." },
    ],
    // CTA band
    bandH2: "Dokumentér arbejdet — og lad kunden skrive under",
    bandBody: "Slut dig til håndværkere der beviser kvaliteten med fotos og underskrift. Intet betalingskort. Gratis for altid.",
  },
  en: {
    eyebrow: "Quality control in Svendetid",
    h1a: "Document the work — ",
    h1b: "with photos",
    h1c: " and the customer's signature",
    sub: "Take photos on site straight from the mobile app, gather everything on the job, and let the customer sign off on the finished work. Proof of what was done, ready to send.",
    cta: "Get started – it's free",
    seePricing: "See pricing",

    trust2: "Free to get started",
    trust3: "Danish support",
    mockReport: "Quality control",
    mockTitle: "Documentation",
    mockPeriod: "This job",
    mockShare: "Share",
    cmpH2: "Proof of the work — not your word against theirs",
    withoutLabel: "Without Svendetid",
    withLabel: "With Svendetid",
    without: [
      "Photos scattered across people's private phones",
      "No documentation when the customer complains later",
      "Signatures on paper that get lost or get wet",
      "Doubt about what was actually done and approved",
    ],
    with: [
      "All photos gathered on the right job — automatically",
      "Time-stamped proof of before, during and after the work",
      "The customer's signature captured digitally and kept forever",
      "One clear, shareable proof of what was approved",
    ],
    introLabel: "How it works",
    introH2: "Documentation that follows the work",
    introSub: "Photos. Signature. Gathered on the job.",
    blocks: [
      {
        label: "Photos from the site",
        h3: "Take the photo. Upload it. Straight from mobile.",
        body: "Snap photos on site with the app and send them straight to the job — before, during and after. Every image is time-stamped and saved on the right task, so you always have proof of what was done. Works offline too; it uploads as soon as there's a connection.",
        chips: [
          { icon: "camera-outline", text: "Take photo" },
          { icon: "cloud-upload-outline", text: "Upload" },
          { icon: "phone-portrait-outline", text: "From mobile" },
        ],
        mock: "photo",
      },
      {
        label: "Customer signature",
        h3: "Let the customer sign off on the finished work",
        body: "Hand the phone or tablet to the customer and let them sign right on the screen once the work is approved. The signature is saved on the job with date and time — a digital sign-off that replaces paper and removes any doubt afterwards.",
        chips: [
          { icon: "create-outline", text: "Sign on screen" },
          { icon: "checkmark-circle", text: "Approved" },
          { icon: "document-text-outline", text: "Saved on job" },
        ],
        mock: "sign",
      },
      {
        label: "Gathered & shareable",
        h3: "All documentation in one place — ready to send",
        body: "Photos, notes and signature gather on the job in the order they happened. Send a finished documentation sheet to the customer, the insurer or the client with one tap — as proof of the quality and the work that was carried out.",
        chips: [
          { icon: "images-outline", text: "Photo gallery" },
          { icon: "paper-plane-outline", text: "Send as PDF" },
          { icon: "lock-closed", text: "Stored securely" },
        ],
        mock: "share",
      },
    ],
    mockJobLabel: "Job · Roof renovation",
    mockPhotoBefore: "Before",
    mockPhotoDuring: "During",
    mockPhotoAfter: "After",
    mockUploaded: "Uploaded",
    mockAddPhoto: "Add photo",
    mockSignTitle: "Work approval",
    mockSignJob: "Roof renovation · Nordby Bolig",
    mockSignBy: "Signed by",
    mockSignName: "Anders Nordby",
    mockSignAt: "Today 14:32",
    mockSignBtn: "Signed",
    mockDocTitle: "Documentation sheet",
    mockDocPhotos: "12 photos",
    mockDocSigned: "Signed",
    mockDocReady: "Ready",
    aiLabel: "AI-driven quality control",
    aiH2a: "Svendetid AI gathers ",
    aiH2b: "the documentation",
    aiBody: "Ask anything: “Are we missing closing photos on any of this week's jobs?” or “Which jobs are finished but not signed off?”. The AI reviews all your jobs and catches what's missing — before the customer does.",
    aiCta: "Try Svendetid AI",
    aiQuestion: "Which finished jobs are missing the customer's signature?",
    aiAnswerIntro: "3 jobs are marked done but missing sign-off:",
    aiRow1: "Roof renovation · Nordby Bolig",
    aiRow1Val: "12 photos",
    aiRow2: "Bathroom renovation · Lund family",
    aiRow2Val: "8 photos",
    aiAnswerOutro: "Both have photos ready — want to send a signature link to the customer?",
    gridH2: "Plus everything you need to document the quality",
    grid: [
      { icon: "camera-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Photos from mobile", body: "Take pictures on site directly in the app and upload them to the job. Before, during and after — always on the right task." },
      { icon: "create-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Digital signature", body: "Let the customer sign on the screen once the work is approved. A digital sign-off that replaces paper." },
      { icon: "time-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Time-stamped proof", body: "Every photo and signature gets date and time automatically. Proof of exactly what was done, and when." },
      { icon: "images-outline", iconBg: "var(--tint-lilac)", iconColor: "#4B3F8F", title: "Gathered photo gallery", body: "All documentation gathered on the job in order. Scroll through the whole journey from start to handover." },
      { icon: "paper-plane-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Send as a document", body: "Generate a finished documentation sheet and send it to the customer, insurer or client with one tap." },
      { icon: "cloud-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Works offline", body: "Take photos with no signal on site. Everything uploads automatically as soon as there's a connection again." },
    ],
    platLabel: "The Svendetid platform",
    platH2: "Quality control is only the beginning",
    platSub: "Quality control is part of the platform — jobs, quotes, time, customers and invoicing in one connected system. Everything talks to everything.",
    platform: [
      { icon: "briefcase-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Jobs", sub: "Run every job", href: "/en/features" },
      { icon: "receipt-outline", iconBg: "var(--orange-100)", iconColor: "var(--accent-strong)", title: "Quotes", sub: "Win more work", href: "/features/offer" },
      { icon: "calendar-outline", iconBg: "#E3ECFB", iconColor: "#2056C0", title: "Calendar", sub: "Plan the crew", href: "/en/features/calendar" },
      { icon: "bar-chart-outline", iconBg: "var(--green-100)", iconColor: "var(--brand)", title: "Reports", sub: "Overview that works", href: "/en/features/reports" },
      { icon: "people-outline", iconBg: "var(--tint-peach)", iconColor: "var(--accent-ink)", title: "Customers", sub: "The whole history", href: "/en/features/customer-database" },
      { icon: "layers-outline", iconBg: "var(--sage-100)", iconColor: "var(--text-muted)", title: "All features", sub: "See the whole platform", href: "/en/features" },
    ],
    secLabel: "Security all the way",
    security: [
      { icon: "shield-checkmark-outline", text: "GDPR" },
      { icon: "cloud-outline", text: "Data in the EU" },
      { icon: "chatbubbles-outline", text: "Support in Danish" },
    ],
    faqH2: "Frequently asked questions",
    faqs: [
      { q: "How do I take photos from mobile?", a: "Open the job in the Svendetid app and tap the camera. The photo is time-stamped and saved automatically on the right task. You can take several in a row — before, during and after — and they gather in one place." },
      { q: "Does photo upload work without internet?", a: "Yes. You can take pictures on site even with no signal. They stay locally on the phone and upload automatically to the job as soon as there's a connection again." },
      { q: "How do I get the customer's signature?", a: "When the work is approved, open sign-off on the phone or tablet and hand it to the customer. They sign directly on the screen, and the signature is saved on the job with date and time." },
      { q: "Is the digital signature valid as proof?", a: "The signature is saved together with name, date, time and the documented work. That gives a clear, traceable proof that the customer approved the work carried out — far stronger than a loose paper note." },
      { q: "Can I forward the documentation?", a: "Yes. Gather photos, notes and signature into one documentation sheet and send it to the customer, insurer or client with one tap — as proof of the quality and the work carried out." },
    ],
    bandH2: "Document the work — and let the customer sign",
    bandBody: "Join trades businesses that prove the quality with photos and signatures. No payment card. Free forever.",
  },
} as const;

type TT = (typeof T)["da"] | (typeof T)["en"];

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18,
  padding: 28, boxShadow: "0 6px 16px rgba(16,33,27,0.06)", display: "flex", flexDirection: "column", gap: 15,
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em",
  textTransform: "uppercase", color: "var(--accent-strong)",
};

function SectionEyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 14 }}>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
      <span style={monoLabel}>{label}</span>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

type Card = { icon: string; iconBg: string; iconColor: string; title: string; body: string };

function FeatureCard({ icon, iconBg, iconColor, title, body }: Card) {
  return (
    <div style={cardStyle}>
      <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={24} color={iconColor} />
      </div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{title}</h3>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{body}</p>
    </div>
  );
}

/* --------------------------- Feature-block mocks ------------------------- */
/* Photo capture — a phone-style card with a before/during/after photo strip. */
function PhotoMock({ t }: { t: TT }) {
  const tiles = [
    { label: t.mockPhotoBefore, src: "/site-before.jpg" },
    { label: t.mockPhotoDuring, src: "/site-during.jpg" },
    { label: t.mockPhotoAfter, src: "/site-after.jpg" },
  ];
  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ width: 30, height: 30, borderRadius: 9, background: "var(--orange-100)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="camera-outline" size={16} color="var(--accent-strong)" /></span>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.mockJobLabel}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {tiles.map((tile) => (
          <div key={tile.label} style={{ borderRadius: 12, overflow: "hidden", aspectRatio: "3 / 4", position: "relative", display: "flex", alignItems: "flex-end", padding: 8 }}>
            <img src={tile.src} alt={tile.label} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            <span style={{ position: "relative", zIndex: 1, fontSize: 10, fontWeight: 700, color: "#fff", background: "rgba(16,33,27,0.52)", borderRadius: 6, padding: "2px 7px", letterSpacing: "0.04em" }}>{tile.label}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--brand)", display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="cloud-upload-outline" size={15} color="var(--brand)" />{t.mockUploaded}</span>
        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: "#fff", background: "var(--accent)", borderRadius: 9, padding: "7px 12px", display: "inline-flex", alignItems: "center", gap: 6, boxShadow: "0 6px 16px rgba(194,65,12,0.26)" }}><Icon name="camera-outline" size={14} color="#fff" />{t.mockAddPhoto}</span>
      </div>
    </div>
  );
}

/* Customer signature — an approval card with a handwritten-style signature line. */
function SignMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", maxWidth: 360, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 20 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.mockSignTitle}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--green-900)", marginTop: 6 }}>{t.mockSignJob}</div>
      <div style={{ marginTop: 16, borderRadius: 12, border: "1px dashed var(--border)", background: "var(--green-50)", padding: "18px 16px 12px", position: "relative" }}>
        {/* stylised signature stroke */}
        <svg viewBox="0 0 240 60" width="100%" height={48} fill="none" aria-hidden>
          <path d="M8 44c14-2 18-30 26-30s6 34 16 34 14-30 24-30 8 24 18 24 16-22 28-22 14 14 26 12 18-10 28-16" stroke="var(--green-700)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div style={{ height: 1, background: "var(--border)", marginTop: 4 }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{t.mockSignBy}: <strong style={{ color: "var(--green-900)" }}>{t.mockSignName}</strong></span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{t.mockSignAt}</span>
        </div>
      </div>
      <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 13, fontWeight: 600, color: "#fff", background: "var(--brand)", borderRadius: 11, padding: "12px 16px" }}>
        <Icon name="checkmark-circle" size={17} color="#fff" />{t.mockSignBtn}
      </div>
    </div>
  );
}

/* Gathered & shareable — a documentation sheet summary card. */
function ShareMock({ t }: { t: TT }) {
  return (
    <div style={{ width: "100%", background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "0 20px 44px rgba(16,33,27,0.16)", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, borderBottom: "1px solid var(--hairline)" }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--tint-lilac)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="document-text-outline" size={18} color="#4B3F8F" /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t.mockDocTitle}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{t.mockJobLabel}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--brand)", background: "var(--tint-mint)", borderRadius: 8, padding: "4px 9px" }}>{t.mockDocReady}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--orange-100)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="images-outline" size={15} color="var(--accent-strong)" /></span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{t.mockDocPhotos}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="checkmark-circle" size={14} color="var(--brand)" />{t.mockUploaded}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--tint-mint)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="create-outline" size={15} color="var(--brand)" /></span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{t.mockSignName}</div>
          <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="checkmark-circle" size={14} color="var(--brand)" />{t.mockDocSigned}</span>
        </div>
        <div style={{ marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "var(--green-900)", background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px" }}>
          <Icon name="paper-plane-outline" size={15} color="var(--accent-strong)" />{t.mockShare}
        </div>
      </div>
    </div>
  );
}

const MOCK_BG: Record<string, string> = {
  photo: "linear-gradient(155deg,var(--orange-100),#FFF7EF)",
  sign: "linear-gradient(155deg,var(--green-50),#EEF6F0)",
  share: "linear-gradient(155deg,var(--tint-lilac),#F4F2FB)",
};

function FeatureBlock({ block, reverse, t }: { block: TT["blocks"][number]; reverse: boolean; t: TT }) {
  const text = (
    <div style={{ order: reverse ? 2 : 1 }}>
      <div style={{ ...monoLabel, marginBottom: 14 }}>{block.label}</div>
      <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 32, lineHeight: 1.12, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{block.h3}</h3>
      <p style={{ fontSize: 17, color: "var(--text-muted)", lineHeight: 1.6, margin: "18px 0 0" }}>{block.body}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 22 }}>
        {block.chips.map((c) => (
          <span key={c.text} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 500, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 999, padding: "7px 13px" }}>
            <Icon name={c.icon} size={15} color="var(--brand)" />{c.text}
          </span>
        ))}
      </div>
    </div>
  );
  const visual = (
    <div style={{ order: reverse ? 1 : 2, background: MOCK_BG[block.mock], borderRadius: 28, padding: "clamp(28px,5%,48px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {block.mock === "photo" && <PhotoMock t={t} />}
      {block.mock === "sign" && <SignMock t={t} />}
      {block.mock === "share" && <ShareMock t={t} />}
    </div>
  );
  return (
    <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
      <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
        {text}
        {visual}
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 16, padding: "20px 22px", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "var(--font-sans)" }}>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: "var(--green-900)" }}>{q}</span>
        <svg viewBox="0 0 24 24" width={18} height={18} fill="none" style={{ color: "var(--accent-strong)", flex: "none", transition: "transform .25s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div style={{ padding: "0 22px 22px", fontSize: 15, lineHeight: 1.6, color: "var(--text-muted)" }}>{a}</div>}
    </div>
  );
}

export default function QualityContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  const aiHref = lang === "en" ? "/en/ai" : "/ai";
  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`
        html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}
      `}</style>

      <LandingNav lang={lang} accent="blue" />

      {/* ===== HERO ===== */}
      <section style={{ background: "#FFFFFF", color: "var(--text-body)", paddingTop: 64 }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "72px 32px 40px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, ...monoLabel, marginBottom: 22 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)" }} />{t.eyebrow}
          </div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 58, lineHeight: 1.03, letterSpacing: "-0.025em", margin: "0 auto", maxWidth: "16ch", color: "var(--green-900)", textWrap: "balance" }}>
            {t.h1a}<span style={{ fontStyle: "italic", color: "var(--brand)" }}>{t.h1b}</span>{t.h1c}
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.6, color: "var(--text-muted)", maxWidth: "60ch", margin: "24px auto 0", textWrap: "pretty" }}>{t.sub}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 34 }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-soft" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "var(--green-900)", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid var(--border)", background: "var(--surface-card)" }}>{t.seePricing}</Link>
          </div>
          <div style={{ display: "flex", gap: 22, justifyContent: "center", alignItems: "center", flexWrap: "wrap", marginTop: 20, fontSize: 13, color: "var(--text-muted)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust2}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="checkmark-circle" size={16} color="var(--brand)" />{t.trust3}</span>
          </div>

          {/* HERO IMAGE — Dokumentation */}
          <div style={{ marginTop: 54, position: "relative" }}>
            <div
              style={{
                position: "relative",
                zIndex: 1,
                padding: 16,
                borderRadius: 14,
                background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 40%, #F0B429 72%, var(--green-900) 100%)",
              }}
            >
              <img
                src="/quality.png"
                alt={t.mockTitle}
                style={{ display: "block", width: "100%", height: "auto", borderRadius: 16 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== WITHOUT / WITH ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "60ch", margin: "0 auto 40px" }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.08, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.cmpH2}</h2>
        </div>
        <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 20, padding: 30 }}>
            <div style={{ ...monoLabel, color: "var(--text-muted)", marginBottom: 18 }}>{t.withoutLabel}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.without.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--tint-rose)", borderRadius: "50%", padding: 3, color: "var(--danger)" }}><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: "var(--green-900)", border: "1px solid #2C3B35", borderRadius: 20, padding: 30, boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, ...monoLabel, color: "var(--accent)", marginBottom: 18 }}>
              <img src="/svendetid-logo.png" alt="" style={{ width: 18, height: 18 }} />{t.withLabel}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {t.with.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <svg viewBox="0 0 24 24" width={22} height={22} style={{ flex: "none", marginTop: 1, background: "var(--brand)", borderRadius: "50%", padding: 3, color: "#fff" }}><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={{ fontSize: 15, color: "var(--text-on-hero)", lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION INTRO ===== */}
      <section style={{ maxWidth: 760, margin: "0 auto", padding: "40px 32px 8px", textAlign: "center" }}>
        <SectionEyebrow label={t.introLabel} />
        <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.introH2}</h2>
        <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0", maxWidth: "48ch" }}>{t.introSub}</p>
      </section>

      {/* ===== THREE FEATURE BLOCKS ===== */}
      {t.blocks.map((block, i) => (
        <FeatureBlock key={block.label} block={block} reverse={i === 1} t={t} />
      ))}

      {/* ===== AI BLOCK ===== */}
      <section style={{ padding: "64px 32px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(32px,5vw,64px)", boxShadow: "0 24px 60px rgba(16,33,27,0.22)", position: "relative", overflow: "hidden" }}>
          <div aria-hidden style={{ position: "absolute", top: "-30%", right: "-10%", width: 420, height: 420, background: "radial-gradient(circle, rgba(249,115,22,0.22), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center", position: "relative" }}>
            <div>
              <div style={{ ...monoLabel, color: "var(--accent)", marginBottom: 16 }}>{t.aiLabel}</div>
              <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--text-on-hero)" }}>{t.aiH2a}<span style={{ fontStyle: "italic", color: "#F4C8A6" }}>{t.aiH2b}</span></h2>
              <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", margin: "20px 0 0", maxWidth: "46ch" }}>{t.aiBody}</p>
              <div style={{ marginTop: 28 }}>
                <Link href={aiHref} className="btn-blue" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.aiCta}<Icon name="arrow-forward" size={18} /></Link>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid #2C3B35", borderRadius: 18, padding: 18, backdropFilter: "blur(4px)" }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: "var(--text-on-hero)", background: "var(--brand)", borderRadius: "14px 14px 4px 14px", padding: "10px 14px", maxWidth: "80%" }}>{t.aiQuestion}</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#FEC935,#FBA932)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="sparkles" size={15} color="#fff" /></span>
                <div style={{ background: "#151D1A", border: "1px solid #2C3B35", borderRadius: "4px 14px 14px 14px", padding: 14, fontSize: 13, color: "var(--text-on-hero)", lineHeight: 1.55 }}>
                  <div style={{ marginBottom: 10 }}>{t.aiAnswerIntro}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(165,116,35,0.16)", borderRadius: 8, marginBottom: 6 }}>
                    <span style={{ flex: 1 }}>{t.aiRow1}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#E3B85F" }}>{t.aiRow1Val}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(165,116,35,0.16)", borderRadius: 8 }}>
                    <span style={{ flex: 1 }}>{t.aiRow2}</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "#E3B85F" }}>{t.aiRow2Val}</span>
                  </div>
                  <div style={{ marginTop: 10, color: "var(--text-hero-muted)" }}>{t.aiAnswerOutro}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURE GRID ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)", textWrap: "balance" }}>{t.gridH2}</h2>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {t.grid.map((f) => <FeatureCard key={f.title} {...f} />)}
        </div>
      </section>

      {/* ===== PLATFORM ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "48px 32px" }}>
        <div style={{ textAlign: "center", maxWidth: "52ch", margin: "0 auto 36px" }}>
          <div style={{ ...monoLabel, marginBottom: 14 }}>{t.platLabel}</div>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.platH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "16px auto 0" }}>{t.platSub}</p>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {t.platform.map((p) => (
            <Link key={p.title} href={p.href} className="card-link" style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 16, textDecoration: "none", color: "inherit" }}>
              <span style={{ width: 38, height: 38, borderRadius: 10, background: p.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name={p.icon} size={19} color={p.iconColor} /></span>
              <span>
                <span style={{ display: "block", fontSize: 14, fontWeight: 600 }}>{p.title}</span>
                <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{p.sub}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== SECURITY ===== */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 32px 8px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "18px 40px", padding: "28px 24px", background: "var(--green-50)", border: "1px solid var(--border)", borderRadius: 18 }}>
          <span style={{ ...monoLabel, color: "var(--text-muted)" }}>{t.secLabel}</span>
          {t.security.map((s) => (
            <span key={s.text} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "var(--green-900)" }}><Icon name={s.icon} size={18} color="var(--brand)" />{s.text}</span>
          ))}
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section style={{ maxWidth: 780, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: "var(--green-900)" }}>{t.faqH2}</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {t.faqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 32px 72px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 28, padding: "clamp(40px,6vw,72px) 32px", textAlign: "center", position: "relative", overflow: "hidden", boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
          <div aria-hidden style={{ position: "absolute", bottom: "-40%", left: "50%", transform: "translateX(-50%)", width: 600, height: 400, background: "radial-gradient(circle, rgba(249,115,22,0.2), rgba(249,115,22,0) 70%)", pointerEvents: "none" }} />
          <img src="/svendetid-logo.png" alt="" style={{ width: 48, height: 48, margin: "0 auto 22px", display: "block", position: "relative" }} />
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 44, lineHeight: 1.08, letterSpacing: "-0.02em", margin: "0 auto", maxWidth: "20ch", color: "#fff", position: "relative", textWrap: "balance" }}>{t.bandH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-hero-muted)", margin: "18px auto 0", maxWidth: "46ch", position: "relative" }}>{t.bandBody}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 30, position: "relative" }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/features/quality-control" enHref="/en/features/quality-control" />
    </div>
  );
}
